// src/pages/personal/analytics/CustomerSection/components/CustomerSegmentation.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Layout';
import { COLORS } from '@/theme';
import { DataList, DataListColumnDef } from '@/components/ui/DataList';
import { SegmentationChart } from './SegmentationChart';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
// Импортируем форматтеры
import { formatPercent, formatNumber } from '@/utils/Formatters';

import styles from '../CustomerSection.module.css';

interface LeadSourceItem {
  source: string;
  name: string;
  count: number;
  percentage: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
  revenue: number;
}

interface CustomerSegmentationProps {
  data: LeadSourceItem[];
  isLoading?: boolean;
}

// Тип для ключей в sourceChartColors объекте
type SourceChartColorKey = keyof typeof COLORS.sourceChartColors;

const CustomerSegmentation: React.FC<CustomerSegmentationProps> = ({ data, isLoading = false }) => {
  const { t, i18n } = useTranslation('customer');
  const { formatPrice } = useBusinessCurrency();
  const currentLocale = i18n.language;
  
  // Функция для получения цвета источника из темы
  const getSourceColor = (source: string): string => {
    const sourceKey = source.toLowerCase();
    return sourceKey in COLORS.sourceChartColors
      ? COLORS.sourceChartColors[sourceKey as SourceChartColorKey]
      : COLORS.sourceChartColors.default;
  };
  
  // Подготовка данных для диаграммы
  const chartData = data.map(item => ({
    name: t(`sources.${item.source}`, { defaultValue: item.name }),
    value: item.count,
    percentage: item.percentage,
    color: getSourceColor(item.source),
    revenue: item.revenue
  }));
  
  // Определение колонок для списка
  const columns: DataListColumnDef<LeadSourceItem>[] = [
    {
      key: 'source',
      title: t('source_distribution.source'),
      width: 'lg',
      render: (item: LeadSourceItem) => (
        t(`sources.${item.source}`, { defaultValue: item.name })
      )
    },
    {
      key: 'count',
      title: t('source_distribution.clients'),
      width: 'sm',
      textAlign: 'right',
      render: (item: LeadSourceItem) => formatNumber(item.count, 0, true, currentLocale)
    },
    {
      key: 'percentage',
      title: t('source_distribution.percentage'),
      width: 'sm',
      textAlign: 'right',
      render: (item: LeadSourceItem) => {
        const trendIcon = item.trend === 'up' 
          ? '↑' 
          : item.trend === 'down' 
            ? '↓' 
            : '−';
        
        const trendClass = item.trend === 'up'
          ? 'text-green-600'
          : item.trend === 'down'
            ? 'text-red-600'
            : 'text-gray-500';
            
        return (
          <span className={trendClass}>
            {trendIcon} {formatPercent(item.percentage, 1, true, currentLocale)}
          </span>
        );
      }
    },
    {
      key: 'revenue',
      title: t('source_distribution.revenue'),
      width: 'md',
      textAlign: 'right',
      render: (item: LeadSourceItem) => formatPrice(item.revenue)
    }
  ];
  
  // Компонент для отображения загрузки
  const renderLoader = () => (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingSpinner}></div>
      <p className={styles.loadingText}>{t('loading')}</p>
    </div>
  );
  
  // Компонент для отображения отсутствия данных
  const renderNoData = () => (
    <div className={styles.noDataContainer}>
      <p>{t('no_data')}</p>
    </div>
  );
  
  // Рендер контента в зависимости от состояния
  const renderContent = () => {
    if (isLoading) {
      return renderLoader();
    }
    
    if (!data || data.length === 0) {
      return renderNoData();
    }
    
    return (
      <div className={styles.distributionContainer}>
        <div className={styles.chartSide}>
          <SegmentationChart data={chartData} />
        </div>
        
        <div className={styles.dataSide}>
          <DataList 
            data={data}
            columns={columns}
            noDataText={t('no_data')}
            compact={true}
            getItemColor={(item) => getSourceColor(item.source)}
          />
        </div>
      </div>
    );
  };
  
  return (
    <Card padding="none" shadow="sm" className="w-full">
      <Card.Header className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{t('source_distribution.title')}</h3>
      </Card.Header>
      <Card.Body className={styles.cardContent}>
        {renderContent()}
      </Card.Body>
    </Card>
  );
};

export default CustomerSegmentation;